'use client'

import { useCallback } from 'react'
import Sentry from '@/lib/sentry-safe'
import { TESLA_REQUIREMENTS } from '@/lib/audio-utils'
import { storageApi } from '@/lib/api/storage'
import { IOS_DOWNLOAD_EVENT } from '@/components/ui/ios-download-overlay'
import { triggerHaptic } from '@/lib/haptics'
import { play } from 'cuelume'

interface DownloadOptions {
  soundId?: string
  fileUrl?: string
  filename?: string
  blob?: Blob
  onBeforeDownload?: () => Promise<void>
  onSuccess?: () => void
  onError?: (error: Error) => void
}

function isIOSSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
  return isIOS && isSafari
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function triggerServerDownload(soundId: string, filename: string): void {
  window.location.href = `/api/sounds/${soundId}/download-file`
}

export function useDownload() {
  const download = useCallback(async (options: DownloadOptions): Promise<{ success: boolean }> => {
    const {
      soundId,
      fileUrl,
      filename = TESLA_REQUIREMENTS.FILENAME,
      blob,
      onBeforeDownload,
      onSuccess,
      onError,
    } = options

    try {
      if (onBeforeDownload) {
        await onBeforeDownload()
      }

      if (blob) {
        triggerBlobDownload(blob, filename)
        triggerHaptic('download-success') // §12.8
        play('success')
        onSuccess?.()
        return { success: true }
      }

      if (isIOSSafari() && soundId && fileUrl) {
        triggerServerDownload(soundId, filename)
        window.dispatchEvent(new CustomEvent(IOS_DOWNLOAD_EVENT))
        triggerHaptic('download-success') // §12.8
        play('success')
        onSuccess?.()
        return { success: true }
      }

      if (fileUrl) {
        // The only branch that actually waits on the network. `loading` marks
        // that user-initiated work has started, so the gap before `success`
        // reads as progress rather than a dead control. The blob and iOS
        // branches above are instant and would only stutter against it.
        play('loading')
        const file = await storageApi.downloadFile(fileUrl)
        triggerBlobDownload(file, filename)
        triggerHaptic('download-success') // §12.8
        play('success')
        onSuccess?.()
        return { success: true }
      }

      throw new Error('Either blob or fileUrl must be provided')
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      Sentry.captureException(err, {
        tags: { context: 'useDownload', isIOSSafari: String(isIOSSafari()) },
      })
      play('error')
      onError?.(err)
      return { success: false }
    }
  }, [])

  return { download, isIOSSafari: typeof window !== 'undefined' ? isIOSSafari() : false }
}
